import type { RouteObject } from 'react-router';

import { lazy, Suspense } from 'react';
import { Outlet, useMatches } from 'react-router';
import { AuthGuard } from '@classroom-ai/ui/auth/guard';
import { LoadingScreen } from '@classroom-ai/ui/components/loading-screen';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { StudentGuard } from 'src/auth/student-guard';

// ----------------------------------------------------------------------

const OverviewPage     = lazy(() => import('src/pages/dashboard/overview'));
const ClassesPage      = lazy(() => import('src/pages/dashboard/classes'));
const SessionPage      = lazy(() => import('src/pages/dashboard/session'));
const PastSessionsPage = lazy(() => import('src/pages/dashboard/past-sessions'));

// ----------------------------------------------------------------------

function SuspenseOutlet() {
  // Keyed by the matched route, not the pathname: a param change (picking another
  // session on /past-sessions/:sessionId?) must not remount the page and lose its state.
  const matches = useMatches();
  const routeId = matches[matches.length - 1]?.id;
  return (
    <Suspense key={routeId} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <StudentGuard>
    <DashboardLayout>
      <SuspenseOutlet />
    </DashboardLayout>
  </StudentGuard>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: CONFIG.auth.skip ? dashboardLayout() : <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      { element: <OverviewPage />, index: true },
      { path: 'classes', element: <ClassesPage /> },
      { path: 'session', element: <SessionPage /> },
      { path: 'past-sessions/:sessionId?', element: <PastSessionsPage /> },
    ],
  },
];
